/**
 * Auto-search-within-map policy.
 *
 * Starts a "search within map" (rectangular-area) search by itself, without the
 * button, when the user pans into territory the original search never covered. From
 * then on the location type is rectangularArea, the URL uses `coordinates`, and
 * searchWithinMapOnMove.policy keeps the rectangle following the map.
 *
 * "Covered" is the backend-suggested bounds of the original search, the area spanning
 * its farthest markers. The drift is ./geometry.js:getOutsideAreaRatio between the
 * visible viewport and those bounds; zooming in or out inside that area never counts,
 * only panning past it does.
 *
 * Applies when:
 *  - the location type is neither State (which keeps its own refresh and location
 *    type) nor already a rectangle;
 *  - a real user gesture produced this position; and
 *  - the drift ratio is at least AUTO_SEARCH_WITHIN_MAP_OUTSIDE_RATIO_THRESHOLD.
 *
 * Decision: `searchRectangle(searchBounds)`. Drift is measured on the viewport; the
 * search itself requests the expanded search bounds.
 *
 * Events:
 *  - `locationSelected`: re-arms the capture of the original bounds.
 *  - `boundsFromBackend`: captures them once while armed. Later backend bounds
 *    (focused narrowing, State refreshes, rectangle re-fetches) describe the current
 *    viewport rather than the searched area, so capturing them would make the drift
 *    check chase the viewport.
 *
 * It runs before focused search so a pan far away becomes a new area search instead of
 * a focused narrowing of the old location.
 *
 * Pure — no store, map, DOM or RxJS access.
 *
 * @module searchResultsMap/services/autoSearchWithinMap.policy
 */

import { getOutsideAreaRatio } from './geometry';
import {
  AUTO_SEARCH_WITHIN_MAP_OUTSIDE_RATIO_THRESHOLD,
  RECTANGULAR_AREA,
  STATE,
} from '../constants';
import { searchRectangle } from './decisions';
import ViewportSearchEvent from '../enums/viewportSearchEvent';

export default function createAutoSearchWithinMapPolicy() {
  const memory = {
    originalBounds: null,
    // Starts armed so the first backend bounds of the page load, which never goes
    // through a location selection, are still captured.
    awaitingOriginalBounds: true,
  };

  function evaluate(snapshot) {
    if (snapshot.locationType === STATE || snapshot.locationType === RECTANGULAR_AREA) {
      return null;
    }

    if (!snapshot.hasUserGesture || !memory.originalBounds) {
      return null;
    }

    const outsideRatio = getOutsideAreaRatio(snapshot.viewport, memory.originalBounds);
    if (outsideRatio < AUTO_SEARCH_WITHIN_MAP_OUTSIDE_RATIO_THRESHOLD) {
      return null;
    }

    return searchRectangle(snapshot.searchBounds);
  }

  function captureOriginalBounds(bounds) {
    if (!memory.awaitingOriginalBounds) {
      return;
    }

    memory.awaitingOriginalBounds = false;
    memory.originalBounds = bounds ?? null;
  }

  function onEvent(event) {
    if (event.type === ViewportSearchEvent.LocationSelected) {
      memory.awaitingOriginalBounds = true;
    }

    if (event.type === ViewportSearchEvent.BoundsFromBackend) {
      captureOriginalBounds(event.bounds);
    }

    return [];
  }

  return {
    name: 'auto-search-within-map',
    evaluate,
    onEvent,
  };
}
